/**
 * Reusable utility function for generating company header HTML for print/PDF
 * 
 * @param {Object} company - Company object with header_image, name, address, etc.
 * @param {String} fileServerUrl - Base URL for file server (e.g., 'http://127.0.0.1:8000/')
 * @param {Object} options - Optional configuration
 * @param {String} options.logoFallback - Fallback logo path (default: '@/assets/image/logo.png')
 * @param {String} options.headerStyle - Additional inline styles for header image container
 * @param {String} options.companyInfoStyle - Additional inline styles for company info container
 * @returns {String} HTML string for company header
 */
export function generatePrintHeader(company, fileServerUrl, options = {}) {
  if (!company) {
    return '';
  }

  const {
    logoFallback = '@/assets/image/logo.png',
    headerStyle = '',
    companyInfoStyle = ''
  } = options;

  const hasHeaderImage = company.header_image;

  // If header image exists, return image header
  if (hasHeaderImage) {
    // Handle path - remove leading slash if present, then combine with fileServerUrl
    const cleanPath = company.header_image.startsWith('/') 
      ? company.header_image.substring(1) 
      : company.header_image;
    const headerImageUrl = fileServerUrl.replace(/\/$/, '') + '/' + cleanPath;
    
    return `
      <div style="width: 100%; margin-bottom: 5px; text-align: center; ${headerStyle}">
        <img src="${headerImageUrl}" alt="Company Header" style="max-width: 100%; height: auto; display: block; margin: 0 0;">
      </div>
    `;
  }

  // Otherwise, return traditional company details header
  let logoUrl = logoFallback;
  if (company.logo_thumb) {
    const cleanLogoPath = company.logo_thumb.startsWith('/') 
      ? company.logo_thumb.substring(1) 
      : company.logo_thumb;
    logoUrl = fileServerUrl.replace(/\/$/, '') + '/' + cleanLogoPath;
  }

  return `
    <div class="invoice-header" style="${companyInfoStyle}">
      <div>
        <h4 class="text-uppercase">${company.name || ''}</h4>
        <p style="width:65%">${company.address || ''}</p>
        <p><i class="mdi mdi-phone-outline"></i> ${company.mobile || ''}</p>
        <p><i class="mdi mdi-email-outline"></i> ${company.email || ''}</p>
        ${company.website ? `<p><i class="mdi mdi-web"></i> ${company.website}</p>` : ''}
      </div>
      <div class="logo-container">
        <img src="${logoUrl}" alt="Logo" class="img-fluid">
      </div>
    </div>
  `;
}

/**
 * Get current company data from store with fallback to localStorage
 * Ensures header_image is included from the latest company list
 * 
 * @param {Object} store - Vuex store instance
 * @returns {Object} Company object with latest data
 */
export function getCurrentCompanyForPrint(store) {
  const companyList = store.getters.getCompanyList;
  let currentCompany = store.getters.getCurrentCompany;

  // If company list has data, find the current company by ID to get latest header_image
  if (companyList && companyList.length > 0 && currentCompany && currentCompany.id) {
    const updatedCompany = companyList.find(c => c.id === currentCompany.id);
    if (updatedCompany) {
      currentCompany = updatedCompany;
    }
  }

  return currentCompany;
}

/**
 * Reusable utility function for generating company footer HTML for print/PDF
 * 
 * @param {Object} company - Company object with footer_image
 * @param {String} fileServerUrl - Base URL for file server (e.g., 'http://127.0.0.1:8000/')
 * @param {Object} options - Optional configuration
 * @param {String} options.footerStyle - Additional inline styles for footer image container
 * @returns {String} HTML string for company footer
 */
export function generatePrintFooter(company, fileServerUrl, options = {}) {
  if (!company) {
    return '';
  }

  const {
    footerStyle = ''
  } = options;

  const hasFooterImage = company.footer_image;

  // If footer image exists, return image footer
  if (hasFooterImage) {
    // Handle path - remove leading slash if present, then combine with fileServerUrl
    const cleanPath = company.footer_image.startsWith('/') 
      ? company.footer_image.substring(1) 
      : company.footer_image;
    const footerImageUrl = fileServerUrl.replace(/\/$/, '') + '/' + cleanPath;
    
    return `
      <div class="print-footer" style="position: fixed; bottom: 0; left: 0; right: 0; width: 100%; text-align: center; ${footerStyle}">
        <img src="${footerImageUrl}" alt="Company Footer" style="max-width: 100%; height: auto; display: block; margin: 0 auto;">
      </div>
    `;
  }

  return '';
}

/**
 * Generate complete header and footer HTML for print/PDF
 * 
 * @param {Object} company - Company object with header_image and footer_image
 * @param {String} fileServerUrl - Base URL for file server
 * @param {Object} options - Optional configuration
 * @returns {String} Complete HTML string including header and footer
 */
export function generatePrintHeaderAndFooter(company, fileServerUrl, options = {}) {
  const headerHTML = generatePrintHeader(company, fileServerUrl, options);
  const footerHTML = generatePrintFooter(company, fileServerUrl, options);
  
  return {
    header: headerHTML,
    footer: footerHTML,
    combined: headerHTML + footerHTML
  };
}

/**
 * Generate complete header HTML with watermark (for invoices/receipts)
 * 
 * @param {Object} company - Company object
 * @param {String} fileServerUrl - Base URL for file server
 * @param {Object} options - Optional configuration
 * @returns {String} Complete HTML string including watermark and header
 */
export function generatePrintHeaderWithWatermark(company, fileServerUrl, options = {}) {
  const {
    logoFallback = '@/assets/image/logo.png',
    watermarkOpacity = 0.1
  } = options;

  let logoUrl = logoFallback;
  if (company?.logo_thumb) {
    const cleanLogoPath = company.logo_thumb.startsWith('/') 
      ? company.logo_thumb.substring(1) 
      : company.logo_thumb;
    logoUrl = fileServerUrl.replace(/\/$/, '') + '/' + cleanLogoPath;
  }

  const watermarkStyle = `
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-image: url('${logoUrl}');
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
    width: 80%; 
    height: 80%;
    opacity: ${watermarkOpacity};
    z-index: -1;
    pointer-events: none;
  `;

  const headerHTML = generatePrintHeader(company, fileServerUrl, options);

  return `
    <div class="container-fluid position-relative">
      <div class="watermark" style="${watermarkStyle}"></div>
      ${headerHTML}
    </div>
  `;
}

